/**
 * Calcoli puri della pagina di firma: separa voci una tantum, mensili e annuali,
 * ricava le rate dal piano di pagamento delle voci e sostituisce i segnaposto
 * {{client.*}} / {{deal.*}} / {{company.*}} nei testi dell'offerta.
 * Nessuna dipendenza da React o Supabase: testabile da solo.
 */

export type ServiceKind = 'one_time' | 'monthly' | 'annual';

export interface SummaryLine {
  id: string;
  title: string;
  description: string | null;
  kind: ServiceKind;
  price: number;
  fullPrice: number;
  months: number | null;
  vatRate: number;
}

export interface Installment {
  label: string;
  percent: number;
  amount: number;
}

export interface SignatureSummary {
  oneTime: SummaryLine[];
  monthly: SummaryLine[];
  annual: SummaryLine[];
  oneTimeTotal: number;
  monthlyTotal: number;
  annualTotal: number;
  firstYearTotal: number;
  installments: Installment[];
  vatRate: number;
}

const DEFAULT_VAT = 22;

const num = (v: unknown): number => {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : 0;
};

export function serviceKind(ds: any): ServiceKind {
  const t = ds?.service?.service_type || ds?.metadata?.custom_service_type || 'one_time';
  if (t === 'monthly') return 'monthly';
  if (t === 'annual' || t === 'yearly') return 'annual';
  return 'one_time';
}

/** "40% alla firma" -> "Alla firma"; testo libero lasciato com'è. */
export function installmentLabel(description: string | null | undefined, index: number): string {
  const raw = (description || '').trim().replace(/^\d+([.,]\d+)?\s*%\s*/, '');
  if (!raw) return `Rata ${index + 1}`;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function buildInstallments(lines: { price: number; schedule: any }[], total: number): Installment[] {
  if (!lines.length || total <= 0) return [];
  // Le rate si mostrano solo se TUTTE le voci una tantum hanno un piano: altrimenti
  // il riepilogo sarebbe parziale e fuorviante.
  if (lines.some((l) => !Array.isArray(l.schedule?.installments) || !l.schedule.installments.length)) return [];
  const order: string[] = [];
  const sums = new Map<string, number>();
  for (const l of lines) {
    const inst = l.schedule.installments as any[];
    inst.forEach((i, idx) => {
      const label = installmentLabel(i?.description, idx);
      const amount = i?.type === 'fixed' ? num(i?.value) : (l.price * num(i?.value)) / 100;
      if (!sums.has(label)) order.push(label);
      sums.set(label, (sums.get(label) || 0) + amount);
    });
  }
  if (order.length < 2) return [];
  return order.map((label) => {
    const amount = Math.round((sums.get(label) || 0) * 100) / 100;
    return { label, amount, percent: Math.round((amount / total) * 1000) / 10 };
  });
}

export function buildSignatureSummary(dealServices: any[]): SignatureSummary {
  const sorted = [...(dealServices || [])].sort((a, b) => num(a?.order) - num(b?.order));
  const oneTime: SummaryLine[] = [];
  const monthly: SummaryLine[] = [];
  const annual: SummaryLine[] = [];
  const schedules: { price: number; schedule: any }[] = [];
  const vatRates = new Set<number>();

  for (const ds of sorted) {
    const fullPrice = num(ds?.amount);
    const price = ds?.discounted_amount != null ? num(ds.discounted_amount) : fullPrice;
    const vatRate = ds?.vat_rate != null ? num(ds.vat_rate) : DEFAULT_VAT;
    vatRates.add(vatRate);
    const line: SummaryLine = {
      id: String(ds?.id ?? Math.random()),
      title: ds?.service?.title || ds?.custom_service_name || 'Servizio',
      description: ds?.description || ds?.service?.description || null,
      kind: serviceKind(ds),
      price,
      fullPrice,
      months: ds?.monthly_duration ? num(ds.monthly_duration) : null,
      vatRate,
    };
    if (line.kind === 'monthly') monthly.push(line);
    else if (line.kind === 'annual') annual.push(line);
    else {
      oneTime.push(line);
      schedules.push({ price, schedule: ds?.payment_schedule });
    }
  }

  const sum = (ls: SummaryLine[]) => Math.round(ls.reduce((s, l) => s + l.price, 0) * 100) / 100;
  const oneTimeTotal = sum(oneTime);
  const monthlyTotal = sum(monthly);
  const annualTotal = sum(annual);
  const firstYearMonthly = monthly.reduce((s, l) => s + l.price * Math.min(12, l.months || 12), 0);

  return {
    oneTime,
    monthly,
    annual,
    oneTimeTotal,
    monthlyTotal,
    annualTotal,
    firstYearTotal: Math.round((oneTimeTotal + firstYearMonthly + annualTotal) * 100) / 100,
    installments: buildInstallments(schedules, oneTimeTotal),
    vatRate: vatRates.size === 1 ? [...vatRates][0] : DEFAULT_VAT,
  };
}

export const withVat = (amount: number, vatRate: number) =>
  Math.round(amount * (100 + vatRate)) / 100;

/** 7200 -> "7.200,00 €" (separatore delle migliaia sempre presente). */
export function formatEuro(amount: number, decimals = 2): string {
  const [int, dec] = Math.abs(amount).toFixed(decimals).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${amount < 0 ? '-' : ''}${grouped}${dec ? ',' + dec : ''} €`;
}

/** Importo compatto per il riepilogo: decimali solo se servono. */
export const formatEuroShort = (amount: number) =>
  formatEuro(amount, Number.isInteger(amount) ? 0 : 2);

/** Riga "23.000 € + 50 €/mese + IVA" sopra il pulsante di firma. */
export function summaryLine(s: SignatureSummary): string {
  const parts: string[] = [];
  if (s.oneTimeTotal > 0) parts.push(formatEuroShort(s.oneTimeTotal));
  if (s.monthlyTotal > 0) parts.push(`${formatEuroShort(s.monthlyTotal)}/mese`);
  if (s.annualTotal > 0) parts.push(`${formatEuroShort(s.annualTotal)}/anno`);
  return parts.length ? `${parts.join(' + ')} + IVA` : '';
}

/**
 * Sostituisce {{client.campo}}, {{deal.campo}}, {{company.campo}} con i valori
 * disponibili; un segnaposto senza valore viene tolto, mai mostrato al cliente.
 */
export function resolvePlaceholders(
  html: string | null | undefined,
  ctx: { client?: any; deal?: any; company?: any },
): string {
  if (!html) return '';
  return html.replace(/\{\{\s*(client|deal|company)\.([a-zA-Z0-9_]+)\s*\}\}/g, (_m, scope, key) => {
    const src = (ctx as any)[scope];
    const v = src?.[key];
    return v == null ? '' : String(v);
  }).replace(/\{\{[^}]*\}\}/g, '');
}

export function formatDateIt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Rome' });
}
